/**
 * Customer Account API – OAuth 2.0 PKCE utilities & endpoint helpers.
 *
 * Reference: https://shopify.dev/docs/api/customer#authentication
 */

// ─── Configuration ─────────────────────────────────────────────────────────────

export function getCustomerAccountConfig() {
  const shopDomain = import.meta.env.VITE_SHOPIFY_STORE_URL; // e.g. varga-test.myshopify.com
  const clientId = import.meta.env.VITE_SHOPIFY_CUSTOMER_ACCOUNT_API_CLIENT_ID;
  const shopId = import.meta.env.VITE_SHOPIFY_SHOP_ID;
  const appUrl = import.meta.env.VITE_APP_URL; // e.g. http://localhost:3000

  return {
    shopDomain,
    clientId,
    shopId,
    appUrl,
    redirectUri: `${appUrl}/auth/callback`,
    postLogoutRedirectUri: `${appUrl}`,
    /** Latest stable API version */
    apiVersion: "2025-01",
  } as const;
}

// ─── Discovery (cached) ───────────────────────────────────────────────────────

interface OpenIDConfig {
  authorization_endpoint: string;
  token_endpoint: string;
  end_session_endpoint: string;
  jwks_uri: string;
  issuer: string;
}

let cachedConfig: OpenIDConfig | null = null;

/**
 * Fetch the OpenID Connect discovery document from the shop's storefront domain.
 * Result is cached in memory for the lifetime of the server process.
 */
export async function discoverAuthEndpoints(): Promise<OpenIDConfig> {
  if (cachedConfig) return cachedConfig;

  const { shopDomain } = getCustomerAccountConfig();
  const res = await fetch(
    `https://${shopDomain}/.well-known/openid-configuration`,
  );

  if (!res.ok) {
    throw new Error(
      `Failed to discover OAuth endpoints: ${res.status} ${res.statusText}`,
    );
  }

  cachedConfig = (await res.json()) as OpenIDConfig;
  return cachedConfig;
}

// ─── PKCE helpers ──────────────────────────────────────────────────────────────

function generateRandomCode(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return String.fromCodePoint.apply(null, Array.from(array));
}

function base64UrlEncode(str: string): string {
  const base64 = btoa(str);
  return base64.replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
}

function convertBufferToString(hash: ArrayBuffer): string {
  const uintArray = new Uint8Array(hash);
  const numberArray = Array.from(uintArray);
  return String.fromCodePoint(...numberArray);
}

/**
 * Generate a random code verifier for PKCE (43-128 chars, base64url-encoded).
 */
export async function generateCodeVerifier(): Promise<string> {
  const rando = generateRandomCode();
  return base64UrlEncode(rando);
}

/**
 * Generate a code challenge from the verifier using SHA-256.
 */
export async function generateCodeChallenge(
  codeVerifier: string,
): Promise<string> {
  const digestOp = await crypto.subtle.digest(
    { name: "SHA-256" },
    new TextEncoder().encode(codeVerifier),
  );
  const hash = convertBufferToString(digestOp);
  return base64UrlEncode(hash);
}

// ─── State / nonce ─────────────────────────────────────────────────────────────

/**
 * Generate a random state string for CSRF protection.
 */
export function generateState(): string {
  const timestamp = Date.now().toString();
  const randomString = Math.random().toString(36).substring(2);
  return timestamp + randomString;
}

/**
 * Generate a random nonce for replay protection.
 */
export function generateNonce(length = 32): string {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let nonce = "";
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    nonce += characters.charAt(randomIndex);
  }
  return nonce;
}

// ─── JWT decoding (for nonce retrieval from id_token) ──────────────────────────

export function decodeJwt(token: string) {
  const [header, payload, signature] = token.split(".");
  const decodedHeader = JSON.parse(atob(header));
  const decodedPayload = JSON.parse(atob(payload));
  return { header: decodedHeader, payload: decodedPayload, signature };
}

export function getNonce(idToken: string): string {
  return decodeJwt(idToken).payload.nonce;
}

// ─── Authorization URL builder ─────────────────────────────────────────────────

export interface AuthorizeParams {
  authorizationEndpoint: string;
  codeChallenge: string;
  state: string;
  nonce: string;
  locale?: string;
}

/**
 * Build the full authorization URL for the Customer Account API OAuth flow.
 */
export function buildAuthorizeUrl(params: AuthorizeParams): string {
  const { clientId, redirectUri } = getCustomerAccountConfig();

  const url = new URL(params.authorizationEndpoint);
  url.searchParams.set("scope", "openid email customer-account-api:full");
  url.searchParams.set("client_id", clientId);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("redirect_uri", redirectUri);
  url.searchParams.set("state", params.state);
  url.searchParams.set("nonce", params.nonce);
  // PKCE for public client
  url.searchParams.set("code_challenge", params.codeChallenge);
  url.searchParams.set("code_challenge_method", "S256");

  if (params.locale) {
    url.searchParams.set("locale", params.locale);
  }

  return url.toString();
}

// ─── Token exchange ────────────────────────────────────────────────────────────

export interface TokenResponse {
  access_token: string;
  expires_in: number;
  id_token: string;
  refresh_token: string;
  token_type: string;
}

/**
 * Exchange an authorization code for tokens (public client with PKCE).
 */
export async function exchangeCodeForTokens(
  tokenEndpoint: string,
  code: string,
  codeVerifier: string,
): Promise<TokenResponse> {
  const { clientId, redirectUri, appUrl } = getCustomerAccountConfig();

  const body = new URLSearchParams();
  body.append("grant_type", "authorization_code");
  body.append("client_id", clientId);
  body.append("redirect_uri", redirectUri);
  body.append("code", code);
  body.append("code_verifier", codeVerifier);

  const response = await fetch(tokenEndpoint, {
    method: "POST",
    headers: {
      "content-type": "application/x-www-form-urlencoded",
      origin: appUrl,
      "user-agent": "PostolarVarga-Storefront/1.0",
    },
    body,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Token exchange failed (${response.status}): ${errorText}`);
  }

  return response.json() as Promise<TokenResponse>;
}

/**
 * Refresh an access token using a refresh token.
 */
export async function refreshAccessToken(
  tokenEndpoint: string,
  refreshToken: string,
): Promise<TokenResponse> {
  const { clientId, appUrl } = getCustomerAccountConfig();

  const body = new URLSearchParams();
  body.append("grant_type", "refresh_token");
  body.append("client_id", clientId);
  body.append("refresh_token", refreshToken);

  const response = await fetch(tokenEndpoint, {
    method: "POST",
    headers: {
      "content-type": "application/x-www-form-urlencoded",
      origin: appUrl,
      "user-agent": "PostolarVarga-Storefront/1.0",
    },
    body,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Token refresh failed (${response.status}): ${errorText}`);
  }

  return response.json() as Promise<TokenResponse>;
}

// ─── Logout URL builder ────────────────────────────────────────────────────────

/**
 * Build the Shopify logout URL.
 * Uses the end_session_endpoint from discovery or falls back to the known
 * Shopify pattern: https://shopify.com/authentication/{shopId}/logout
 */
export function buildLogoutUrl(
  endSessionEndpoint: string | null,
  idToken: string,
): string {
  const { shopId, postLogoutRedirectUri } = getCustomerAccountConfig();
  const endpoint =
    endSessionEndpoint ?? `https://shopify.com/authentication/${shopId}/logout`;
  const url = new URL(endpoint);
  url.searchParams.set("id_token_hint", idToken);
  url.searchParams.set("post_logout_redirect_uri", postLogoutRedirectUri);
  return url.toString();
}

// ─── Customer Account API GraphQL ──────────────────────────────────────────────

/**
 * Get the Customer Account API GraphQL endpoint URL.
 *
 * The Customer Account API is hosted on `shopify.com`, NOT on the shop's
 * custom/myshopify domain. The canonical format is:
 *   https://shopify.com/{shopId}/account/customer/api/{version}/graphql
 *
 * Ref: https://shopify.dev/docs/api/customer
 */
export function getCustomerApiEndpoint(): string {
  const { shopId, apiVersion } = getCustomerAccountConfig();
  return `https://shopify.com/${shopId}/account/customer/api/${apiVersion}/graphql`;
}
